import { z } from "zod";
//#region src/dp-sizing.ts
/**
* GCP machine-type name shape for a base pool's `nodeType`. Mirrors the XRD
* `spec.provisionedNodeTypes[].nodeType` pattern + maxLength (node-pool demand-provisioning R1 / S-3):
* the YAML / resource-name injection defense on every pool nodeType. Catalog MEMBERSHIP (does this SKU
* exist in the DP's region?) is WS-3's fail-closed check — NOT enforced here.
*/
const POOL_NODE_TYPE_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
/**
* Max length of a BASE-pool (infra/default-app) machine-type name = 28 (E-9 pool-naming contract).
* TIGHTER than the demand-pool cap (40, `provisionedNodeTypeSchema.nodeType` in machine-type-catalog.ts):
* the reroll pool's GKE `crossplane.io/external-name` (nodePoolId — GKE-capped at 40) is `<kind>-<type>`,
* and the longest role prefix is `default-app-` (12) → the type must be <= 28 to stay in GKE's budget.
* Real SKUs are <=~20 chars, so 28 rejects only pathological input, and it fails at schema validation
* (early/clear) rather than at Crossplane apply (late/confusing). Demand pools keep 40 (bare `<type>`
* external-name, no role prefix, so full budget).
*/
const POOL_NODE_TYPE_MAX_LEN = 28;
/** A base-pool machine type (infra pool OR default-app pool). Shape-validated; catalog check is WS-3. */
const poolNodeTypeSchema = z.string().min(1).max(28).regex(POOL_NODE_TYPE_PATTERN, { message: "nodeType must be a lowercase GCP machine-type name (e.g. e2-standard-4)" });
/** Max nodes in a single base pool (infra/default-app). Mirrors the legacy create-DTO `nodeCount` cap. */
const POOL_NODE_COUNT_MAX = 100;
/** Max replicas for a DP-platform workload (nats/coreApi/shell). A fat-finger guard, not a quorum rule. */
const REPLICA_COUNT_MAX = 10;
/** Memorystore Redis memory floor (GiB). */
const REDIS_MEMORY_SIZE_GB_MIN = 1;
/** Memorystore Redis memory ceiling (GiB) — Memorystore Standard tier max. */
const REDIS_MEMORY_SIZE_GB_MAX = 300;
/**
* Base-pool autoscale bounds. `minNodes` >= 0 (the default-app pool scales to 0 when idle); `maxNodes`
* >= 1; and `minNodes <= maxNodes` (an inverted window would render an invalid GKE autoscaling config).
* `.strict()` — unknown keys rejected (mass-assignment defense on the XR-write + request paths).
*/
const nodePoolAutoscaleSchema = z.object({
	minNodes: z.number().int().min(0).max(100),
	maxNodes: z.number().int().min(1).max(100)
}).strict().refine((v) => v.minNodes <= v.maxNodes, {
	message: "autoscale.minNodes must be <= autoscale.maxNodes",
	path: ["minNodes"]
});
/**
* One base pool's sizing (the `{xr}-infra-pool` OR the `{xr}-default-app-pool`): machine type + a fixed
* node count + autoscale bounds. Both pools use this identical shape; their DEFAULTS differ (applied by
* the XRD + WS-3, not here — see 00-index "Defaults when omitted"). `.strict()`.
*/
const nodePoolSizingSchema = z.object({
	nodeType: poolNodeTypeSchema,
	nodeCount: z.number().int().min(0).max(100),
	autoscale: nodePoolAutoscaleSchema
}).strict();
/**
* Max length of a GKE NodePool id (`cloud.google.com/gke-nodepool` label / drain selector) = 40. This is
* the ROLE-QUALIFIED pool name `<role>-<type>` (e.g. `default-app-e2-standard-4`) — the longest role
* prefix `default-app-` (12) + a ≤28 base-pool type = 40 (which is exactly why the base-pool nodeType is
* capped at 28). Used by `drainOldPool` below — WIDER than `POOL_NODE_TYPE_MAX_LEN` (which caps a BARE
* machine type), because `drainOldPool` carries the full role-qualified nodepool id.
*/
const POOL_NODEPOOL_ID_MAX_LEN = 40;
/**
* A ROLE-QUALIFIED GKE NodePool id `<role>-<type>` (e.g. `default-app-e2-standard-4`) — the value
* carried by `drainOldPool`. Requires the `(infra|default-app)-` prefix (NOT a bare machine type),
* mirroring the PoolDrain CRD's non-empty `nodePoolId` pattern so the CP-side contract and the DP-side
* CRD agree.
*/
const POOL_NODEPOOL_ID_PATTERN = /^(infra|default-app)-[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
/** A GKE NodePool id (role-qualified `<role>-<type>`) — the value carried by `drainOldPool`. */
const poolNodePoolIdSchema = z.string().min(1).max(40).regex(POOL_NODEPOOL_ID_PATTERN, { message: "drainOldPool must be a role-qualified GKE nodepool id (e.g. default-app-e2-standard-4)" });
/**
* XR-INTERNAL base-pool shape = `nodePoolSizingSchema` PLUS the two transient reroll fields
* (DECISIONS E-9, revised by R-1 + the drain-pivot E-9-REVISED). Both are **NOT caller/DTO fields** —
* written EXCLUSIVELY by `PoolRerollService` (WS-3), read by the composition:
*  - `surgeNodeType` (≤28, a bare machine type): the replacement pool's type during surge (set from the
*    already-catalog-validated `nodeType`).
*    ⛔ **CORRECTED 2026-08-08 (P-1). This said "GKE machineType is immutable — DEC-3". It is NOT.**
*    MEASURED against the provider image actually running on `zion-cp-dev`
*    (`provider-gcp-container:v1.8.3`, `sha256:cdaffccdbab0…`): at its pinned
*    `terraform-provider-google` commit `5093dfd2a5ea`, `nodePoolUpdate` has an explicit **in-place**
*    branch on `node_config.0.{disk_size_gb,disk_type,machine_type}` that calls the GKE
*    `NodePools.Update` API, and `machine_type` carries **no `ForceNew`** (its sibling `metadata`
*    does — a positive control four lines away). `gcloud container node-pools update --machine-type`
*    exists. The installed CRD has zero immutability markers. ⇒ an in-place change is an UPDATE.
*    ⚠️ The *outer* `node_config` LIST does carry `ForceNew: true` — that governs the list count, not
*    nested fields. Conflating the two is how eight places in this tree reached the opposite answer
*    from real evidence.
*    ⭐ **The surge/drain/promote design this field serves is still CORRECT, and for a better reason:**
*    an in-place update makes GKE roll every node in the pool under its own SURGE policy, which does
*    NOT respect this platform's drain/PDB sequencing. We create-new → drain-old → delete-old because
*    we want to own the eviction, not because the API forbids the patch.
*    Full write-up + controls: `requirements/architecture/plans/2026-08-07-console-r9/P-1-crossplane-render-artifact.md`.
*  - `drainOldPool` (≤40, a role-qualified nodepool id `<role>-<oldType>`): the OLD pool's
*    `cloud.google.com/gke-nodepool` id, written during the DRAIN phase so the composition can cordon/
*    drain it; empty/absent = no drain.
* This schema models the on-XR spec (mirrors the XRD 1:1) so WS-3 can read/validate the XR + reroll state
* with a typed shape; the create AND update DTOs use the plain `nodePoolSizingSchema` (neither field).
* Both OPTIONAL, no default (absent = steady state). `.strict()`.
*/
const nodePoolSizingWithSurgeSchema = nodePoolSizingSchema.extend({
	surgeNodeType: poolNodeTypeSchema.optional(),
	drainOldPool: poolNodePoolIdSchema.optional()
}).strict();
/** A single replica count, range-checked [1, REPLICA_COUNT_MAX]. */
const replicaCountSchema = z.number().int().min(1).max(10);
/**
* Static replica-count sizing for a DP-platform workload (coreApi / shell). E-Q2 (supersedes E-3):
* there is NO per-DP HPA field — `replicas` is the sole explicit replica knob; WS-2 applies a static
* uniform chart HPA default (not a per-DP XR field). `.strict()`. NATS uses `natsSizingSchema` below
* since Stage C of plan 2026-08-18-nats-dedicated-pool-and-sizing.
*/
const replicaSizingSchema = z.object({ replicas: replicaCountSchema }).strict();
/** Max NATS replicas on a dedicated pool: one pod per node, one node per zone, three zones, R3 streams. */
const NATS_DEDICATED_POOL_REPLICAS_MAX = 3;
/** Dedicated-pool autoscale window. Same shape as `nodePoolAutoscaleSchema`, with `minNodes` >= 1. */
const natsAutoscaleSchema = z.object({
	minNodes: z.number().int().min(1).max(100),
	maxNodes: z.number().int().min(1).max(100)
}).strict().refine((v) => v.minNodes <= v.maxNodes, {
	message: "autoscale.minNodes must be <= autoscale.maxNodes",
	path: ["minNodes"]
});
/**
* NATS sizing (plan 2026-08-18-nats-dedicated-pool-and-sizing, Stage C, index row I-1).
*
* `replicas` is the JetStream cluster size and the StatefulSet replica count. `nodeType` opts the DP
* into a dedicated, tainted NATS node pool of that GCE machine type; absent means NATS stays on the
* infra pool. `autoscale` bounds that pool per zone; absent means the XRD defaults (1 and 1, one node
* per zone). Once `nodeType` is set, provisioning-svc refuses a different value with 409 (ruling R-008):
* the pool name is type-keyed, so a type change would delete the live pool under the pods.
*
* `diskType` is deliberately NOT here. provisioning-svc computes it with `bootDiskTypeForMachineType`
* at every XR write (ruling R-009), the way `dedicatedNodePools[].diskType` is computed, because the
* render-time pairing of machine type and boot disk is the only control that prevents a wedged pool
* (CP-029). `.strict()`, so a caller-supplied `diskType` is rejected at the schema.
*
* Two bounds tie the pool to the pods it must carry (ruling R-023). `autoscale.minNodes` is at least 1:
* the StatefulSet's store is an emptyDir on the pool's nodes, and a pool at 0 nodes would leave every
* NATS pod Pending while GCE never validated the machine-disk pairing (CP-029's silent class). And with
* `nodeType` set, `replicas` is at most `NATS_DEDICATED_POOL_REPLICAS_MAX`: required hostname anti-affinity
* gives one pod per node, the pool gives one node per zone, the target cluster has three node locations,
* and three is the stream replica factor, so a fourth server adds no durability. Without `nodeType` the
* shared `replicaCountSchema` bound stands (the plan-map path).
*/
const natsSizingSchema = z.object({
	replicas: replicaCountSchema,
	nodeType: poolNodeTypeSchema.optional(),
	autoscale: natsAutoscaleSchema.optional()
}).strict().refine((v) => v.nodeType === void 0 || v.replicas <= 3, {
	message: `nats.replicas must be <= 3 when nodeType selects a dedicated pool`,
	path: ["replicas"]
});
/**
* DP Redis sizing knob exposed on the create/update surface. Only `memorySizeGb` is caller-settable;
* `version` remains XRD-defaulted. `.strict()`.
*/
const redisSizingSchema = z.object({ memorySizeGb: z.number().int().min(1).max(300) }).strict();
/**
* The whole explicit DP-sizing surface, EVERY field OPTIONAL — a bare create/patch still works (omitted
* fields fall back to the XRD `default:` and, during the `plan` transition, WS-3's plan->explicit
* backfill). `.strict()` — unknown keys rejected. The create + update DTOs embed these exact field
* schemas; WS-3 re-parses this composite when backfilling/validating a running-DP sizing PATCH. This is
* the CALLER surface (create + update) — infra/defaultApp use the plain `nodePoolSizingSchema` (NO
* `surgeNodeType`; surge is XR-internal per R-1, written only by WS-3's PoolRerollService).
*/
const dpSizingSchema = z.object({
	infra: nodePoolSizingSchema.optional(),
	defaultApp: nodePoolSizingSchema.optional(),
	nats: natsSizingSchema.optional(),
	coreApi: replicaSizingSchema.optional(),
	shell: replicaSizingSchema.optional(),
	redis: redisSizingSchema.optional()
}).strict();
//#endregion
export { NATS_DEDICATED_POOL_REPLICAS_MAX, POOL_NODEPOOL_ID_MAX_LEN, POOL_NODEPOOL_ID_PATTERN, POOL_NODE_COUNT_MAX, POOL_NODE_TYPE_MAX_LEN, POOL_NODE_TYPE_PATTERN, REDIS_MEMORY_SIZE_GB_MAX, REDIS_MEMORY_SIZE_GB_MIN, REPLICA_COUNT_MAX, dpSizingSchema, natsAutoscaleSchema, natsSizingSchema, nodePoolAutoscaleSchema, nodePoolSizingSchema, nodePoolSizingWithSurgeSchema, poolNodePoolIdSchema, poolNodeTypeSchema, redisSizingSchema, replicaCountSchema, replicaSizingSchema };

//# sourceMappingURL=dp-sizing.js.map